/**
 * How Sokuji gets a model, and why this page names no price.
 *
 * The three routes are set out as equals rather than as a ladder with the
 * managed account at the top: the whole position is that you may never touch
 * that service, and a page that ranked it would say otherwise.
 */

import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { PageHead, Section } from '@/components/site/Page';
import '@/components/site/Page.scss';
import { PROVIDER_ROWS } from '@/components/site/providers';
import './Providers.scss';

/** The three cards, in the design's order: yours, yours, then ours. */
const ROUTES = [
  { id: 'local', marked: true },
  { id: 'own', marked: false },
  { id: 'managed', marked: false },
] as const;

export function Providers() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="site-page providers">
      <PageHead
        kicker={t('nav.providers')}
        title={t('providers.page.title')}
        lead={t('providers.page.lead')}
      />

      <Section title={t('providers.routes.title')}>
        <div className="providers__routes">
          {ROUTES.map(({ id, marked }) => (
            <article
              key={id}
              // The route that asks nothing of anyone gets the outline: it is
              // the one the rest of the site keeps pointing at.
              className={`providers__route ${marked ? 'providers__route--marked' : ''}`}
            >
              <h3>{t(`providers.${id}.title`)}</h3>
              <p>{t(`providers.${id}.body`)}</p>
              <p className="site-label providers__chip">{t(`providers.${id}.chip`)}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section title={t('providers.table.title')} lead={t('providers.table.lead')}>
        <div className="providers__table" role="table">
          <div className="providers__row providers__row--head" role="row">
            <span className="site-label" role="columnheader">{t('providers.col.provider')}</span>
            <span className="site-label" role="columnheader">{t('providers.col.runs')}</span>
            <span className="site-label" role="columnheader">{t('providers.col.speaks')}</span>
            <span className="site-label" role="columnheader">{t('providers.col.faceToFace')}</span>
            <span className="site-label" role="columnheader">{t('providers.col.setup')}</span>
          </div>
          {PROVIDER_ROWS.map((p) => (
            <div className="providers__row" role="row" key={p.name}>
              <span className="providers__name" role="rowheader">
                {p.name}
              </span>
              <span role="cell">
                {/* The vendor is substituted rather than translated into the
                    sentence: it is a company name, and which one it is is the
                    only reason this column exists. */}
                {t(`providers.run.${p.runs}`).replace('{vendor}', p.vendor ?? '')}
              </span>
              <span role="cell">{t(`providers.speaks.${p.speaks}`)}</span>
              <span role="cell">{t(p.faceToFace ? 'providers.faceToFace.yes' : 'providers.faceToFace.no')}</span>
              <span role="cell">
                <Link to={lp(`/docs/${p.guide}`)}>{t('providers.guide')}</Link>
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title={t('providers.cost.title')} lead={t('providers.cost.lead')}>
        <div className="providers__cost">
          <article className="providers__cost-card">
            <div className="site-label providers__cost-label">
              {t('providers.cost.metered.title')}
            </div>
            <p>{t('providers.cost.metered.body')}</p>
            <p>{t('providers.cost.metered.body2')}</p>
          </article>

          {/* Dark, because it is the answer to the objection the section
              raises: paying us does not move us into the path. */}
          <article className="providers__cost-card providers__cost-card--dark">
            <div className="site-label providers__cost-label providers__cost-label--alt">
              {t('providers.cost.path.title')}
            </div>
            <p>
              {t('providers.cost.path.body')}{' '}
              <Link to={lp('/privacy')}>{`${t('providers.cost.wired')} \u2192`}</Link>
            </p>
          </article>
        </div>

        <p className="providers__note">{t('providers.cost.note')}</p>
      </Section>
    </div>
  );
}
